<template>
  <v-container fluid class="pa-0">
    <!-- Header -->
    <div class="d-flex justify-space-between align-center mb-7">
      <div>
        <div style="font-size: 30px; font-weight: 800">My OJT Requirements</div>
        <div class="text-subtitle-2 mt-1">
          <span class="text-grey"
            >Upload and manage your required OJT documents</span
          >
        </div>
      </div>
    </div>

    <!-- Search bar -->
    <v-card flat class="pa-4 rounded-xl mb-5 search-card">
      <v-text-field
        v-model="search"
        placeholder="Search requirements..."
        prepend-inner-icon="mdi-magnify"
        variant="outlined"
        density="compact"
        hide-details
        clearable
      />
    </v-card>

    <!-- Data Table -->
    <v-card flat class="rounded-xl table-card">
      <v-data-table
        :headers="headers"
        :items="table_data"
        :search="search"
        :loading="loading"
        hover
        class="rounded-xl"
      >
        <template v-slot:[`item.submission_date`]="{ item }">
          <span v-if="item.submission_date">{{
            formatDate(item.submission_date)
          }}</span>
          <span v-else class="text-grey text-caption font-italic"
            >Not submitted</span
          >
        </template>

        <template v-slot:[`item.status`]="{ item }">
          <v-chip
            v-if="item.status == 1"
            size="x-small"
            color="warning"
            variant="tonal"
            >For Approval</v-chip
          >
          <v-chip
            v-else-if="item.status == 2"
            size="x-small"
            color="success"
            variant="tonal"
            >Approved</v-chip
          >
          <v-chip
            v-else-if="item.status == 3"
            size="x-small"
            color="error"
            variant="tonal"
            >Returned</v-chip
          >
          <v-chip v-else size="x-small" variant="outlined" color="grey"
            >No Submission</v-chip
          >
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex align-center gap-1 py-1">
            <v-btn
              v-if="!item.requirement_submission_id && item.hasTmp"
              size="small"
              color="primary"
              variant="tonal"
              prepend-icon="mdi-download"
              rounded="lg"
              @click="downloadFile(item)"
              >Download template</v-btn
            >
            <v-btn
              v-if="item.requirement_submission_id && item.status != 3"
              size="small"
              color="primary"
              variant="tonal"
              prepend-icon="mdi-eye-outline"
              rounded="lg"
              @click="viewItem(item)"
              >View</v-btn
            >

            <v-btn
              v-if="!item.requirement_submission_id && item.enabled"
              size="small"
              color="success"
              variant="tonal"
              prepend-icon="mdi-upload-outline"
              rounded="lg"
              @click="uploadItem(item)"
              >Upload</v-btn
            >

            <template
              v-if="
                item.requirement_submission_id &&
                item.status == 3 &&
                item.enabled
              "
            >
              <v-btn
                size="small"
                color="primary"
                variant="tonal"
                prepend-icon="mdi-pencil-outline"
                rounded="lg"
                @click="editItem(item)"
                >Edit</v-btn
              >
              <v-btn
                size="small"
                color="error"
                variant="tonal"
                icon="mdi-delete-outline"
                rounded="lg"
                @click="deleteItem(item)"
              />
            </template>
          </div>
        </template>

        <template #no-data>
          <div class="text-center py-10">
            <v-icon size="44" color="grey-lighten-1"
              >mdi-file-document-outline</v-icon
            >
            <div class="text-body-2 text-grey mt-2">No requirements found.</div>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Dialogs -->
    <SubmitRequirementsDialog :data="itemData" :action="action" />

    <v-dialog v-model="dialogConfirmDelete" max-width="440">
      <v-card rounded="xl">
        <v-card-title class="text-body-1 font-weight-bold pa-5 pb-2"
          >Confirm Deletion</v-card-title
        >
        <v-card-text class="text-body-2 text-grey-darken-1 pb-4">
          Are you sure you want to delete this submission? This action cannot be
          undone.
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer />
          <v-btn variant="text" @click="dialogConfirmDelete = false"
            >Cancel</v-btn
          >
          <v-btn
            color="error"
            variant="flat"
            rounded="lg"
            @click="confirmDelete"
            :loading="confirmBtnLoading"
            :disabled="confirmBtnLoading"
            >Delete</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <fade-away-message-component
      displayType="default"
      v-model="fadeAwayMessage.show"
      :message="fadeAwayMessage.message"
      :header="fadeAwayMessage.header"
      :type="fadeAwayMessage.type"
    />
  </v-container>
</template>

<script>
import eventBus from "@/eventBus";
import SubmitRequirementsDialog from "../Dialogs/Forms/SubmitRequirementsDialog.vue";
export default {
  components: {
    SubmitRequirementsDialog,
  },
  data: () => ({
    search: "",
    headers: [
      { title: "Requirement", value: "requirement_name", align: "start" },
      { title: "Status", value: "status", align: "center" },
      { title: "Submission Date", value: "submission_date", align: "center" },
      {
        title: "Actions",
        value: "actions",
        align: "center",
        sortable: false,
        width: 200,
      },
    ],
    data: [],
    totalCount: 0,
    deleteData: null,
    itemData: null,
    uploadData: null,
    alerted: false,
    loading: false,
    options: {},
    action: null,
    paginationData: {},
    formdata: [],
    table_data: [],
    work_dates_menu: false,
    dialogConfirmDelete: false,
    confirmBtnLoading: false,
    fadeAwayMessage: {
      show: false,
      type: "success",
      header: "System Message!",
      message: "",
      top: 10,
    },
  }),
  mounted() {
    this.initialize();
    eventBus.on("closeSubmitRequirementsDialog", () => {
      this.initialize();
    });
  },
  beforeUnmount() {
    eventBus.off("closeSubmitRequirementsDialog");
  },
  methods: {
    pagination(data) {
      this.paginationData = data;
    },
    initialize() {
      this.loading = true;
      this.axiosCall(
        "/student-requirement-submission/getMy/getRequirements",
        "GET",
      ).then((res) => {
        if (res) {
          console.log(res.data);
          this.table_data = res.data;
          this.loading = false;
        }
      });
    },
    uploadItem(item) {
      this.itemData = item;
      this.action = "Upload";
    },
    deleteItem(item) {
      this.dialogConfirmDelete = true;
      this.deleteData = item;
    },
    editItem(item) {
      this.itemData = item;
      this.action = "Update";
    },
    viewItem(item) {
      this.itemData = item;
      this.action = "View";
    },

    downloadFile(item) {
      let url =
        process.env.VUE_APP_SERVER +
        "/student-requirement-submission/get-template/" +
        item.file_template;

      let token = localStorage.getItem("token");

      fetch(url, { headers: { Authorization: `Bearer ${token}` } })
        .then((response) => response.blob())
        .then((blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", item.file_template);
          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch((err) => {
          console.log(err);
        });
    },

    confirmDelete() {
      this.confirmBtnLoading = true;
      this.axiosCall(
        "/student-requirement-submission/" +
          this.deleteData.requirement_submission_id,
        "DELETE",
      ).then((res) => {
        if (res.data.status == 200) {
          this.fadeAwayMessage.show = true;
          this.fadeAwayMessage.type = "success";
          this.fadeAwayMessage.message = res.data.msg;
          this.fadeAwayMessage.header = "System Message";
          this.dialogConfirmDelete = false;
          this.confirmBtnLoading = false;
          this.initialize();
        } else {
          this.confirmBtnLoading = false;
          this.fadeAwayMessage.show = true;
          this.fadeAwayMessage.type = "error";
          this.fadeAwayMessage.message = res.data.msg;
          this.fadeAwayMessage.header = "System Message";
        }
      });
    },
  },
};
</script>

<style scoped>
.search-card {
  border: 1px solid #d1d5db !important;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06) !important;
}
.table-card {
  border: 1px solid #d1d5db !important;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06) !important;
}
.gap-1 {
  gap: 4px;
}
</style>
